import {isFilled} from '@prismicio/helpers';
import type {ContentRelationshipField} from '@prismicio/types';
import type {PinnedContentHeroSlice} from '@prismicio-types';
import type {FilledContent} from '~~/app/composables/contentHelpers';

// ─── Content link ───────────────────────────────────────────────

interface ResolvedContentLink {
    content: FilledContent | undefined;
    pinned: boolean;
}

function isFilledLink(link: ContentRelationshipField | null | undefined): boolean {
    return !!link && isFilled.contentRelationship(link);
}

function asUsableContent(link: ContentRelationshipField | null | undefined): FilledContent | undefined {
    if (!link || !isFilled.contentRelationship(link) || link.isBroken) return undefined;
    return link as FilledContent;
}

function resolveContentLink(
    pressRelease: ContentRelationshipField | null | undefined,
    original: ContentRelationshipField | null | undefined,
): ResolvedContentLink {
    return {
        content: asUsableContent(pressRelease) ?? asUsableContent(original),
        // Either link still counts as a pin when its document was deleted.
        pinned: isFilledLink(pressRelease) || isFilledLink(original),
    };
}

// ─── Pinned content ─────────────────────────────────────────────

function mainLink(slice: PinnedContentHeroSlice): ResolvedContentLink {
    const pressRelease = slice.variation === 'default' ? slice.primary.newMainContent : undefined;
    return resolveContentLink(pressRelease, slice.primary.mainContent);
}

function sideLinks(slice: PinnedContentHeroSlice): ResolvedContentLink[] {
    if (slice.variation !== 'multipleContent') return [];
    return slice.items.map((item) => resolveContentLink(item.newSideContent, item.sideContent));
}

function filledSideContents(slice: PinnedContentHeroSlice): FilledContent[] {
    return sideLinks(slice).flatMap((link) => (link.content ? [link.content] : []));
}

export function hasPinnedContent(slice: PinnedContentHeroSlice): boolean {
    if (slice.variation === 'default') return mainLink(slice).pinned;
    if (mainLink(slice).content) return true;
    if (slice.variation !== 'multipleContent') return false;
    // A broken side link still counts as pinned, so the slice does not fall back to search results.
    return sideLinks(slice).some((link) => link.pinned);
}

export function pinnedHeroContent(slice: PinnedContentHeroSlice): FilledContent | undefined {
    const main = mainLink(slice).content;
    if (slice.variation !== 'multipleContent') return main;
    return main ?? filledSideContents(slice)[0];
}

export function pinnedSideContents(slice: PinnedContentHeroSlice): FilledContent[] {
    const sides = filledSideContents(slice);
    if (mainLink(slice).content) return sides;
    // When the main link is empty, the first side link is shown as the hero.
    return sides.slice(1);
}

// ─── Display date ───────────────────────────────────────────────

export type DatedContent = FilledContent & {date?: Date};

export function withDisplayDate(items: FilledContent[]): DatedContent[] {
    return items.map((item) => ({...item, date: displayDate(item)}));
}

function displayDate(item: FilledContent): Date | undefined {
    if (item.type === 'event' && item.data?.date) {
        return new Date(item.data.date);
    }
    if (
        (item.type === 'article' || item.type === 'guide' || item.type === 'successStory' || item.type === 'pressRelease') &&
        item.data?.releaseDate
    ) {
        return new Date(item.data.releaseDate);
    }
    return undefined;
}
